/**
 * The Team tab's test call (docs/plans/oncall-line-plan.md 7.5 POST
 * /members/:id/test-call, 7.7, 8, 11 step 5; WP-11), as pure helpers.
 *
 * The flow (plan 16, WP-08): POST /members/:id/test-call answers 202 { ok,
 * call_id } once the call is placed; the member's cell rings with "This is a
 * test of the West Clinical Research 24-hour line. Press 1 to confirm."; the
 * result lands later on the member row (last_test_at, last_test_ok), a pass
 * or a failure alike. The tab therefore polls GET /members every
 * TEST_POLL_MS until last_test_at changes, and gives up quietly after
 * TEST_POLL_LIMIT_MS (the row still shows whatever arrives later). 409
 * ONCALL_TEST_IN_PROGRESS means a test to that member is already ringing.
 *
 * While ONCALL_DIAL_OVERRIDE_TO is set every leg rings the override number
 * and the member row is left alone (plan 8; plan 16, WP-08), so no result
 * will come: the tab says so and does not poll.
 */
import type { OncallHealth, OncallMember } from '../../api/oncallApi';

/** How often the tab re-reads the list while a test call is out. */
export const TEST_POLL_MS = 5000;

/** How long it waits for a result: a 40 s ring, the whisper and the press-1 gather fit well inside. */
export const TEST_POLL_LIMIT_MS = 120000;

export type TestOutcome = 'passed' | 'failed';

/** A test call the tab is waiting on. */
export interface PendingTest {
  memberId: string;
  /** The member's last_test_at when the test was placed (the result is a change of it). */
  beforeAt: string | null;
  /** Date.now() when the test was placed. */
  startedMs: number;
}

/**
 * The result of a pending test, read from the member row as GET /members
 * answers it: 'passed' or 'failed' once last_test_at has changed to a new
 * time, else null (still ringing, or no result yet).
 */
export function testOutcome(pending: Pick<PendingTest, 'beforeAt'>, member: Pick<OncallMember, 'last_test_at' | 'last_test_ok'> | null | undefined): TestOutcome | null {
  if (!member || !member.last_test_at) return null;
  if (member.last_test_at === pending.beforeAt) return null;
  if (member.last_test_ok === true) return 'passed';
  if (member.last_test_ok === false) return 'failed';
  return null;
}

/**
 * Why a test call cannot be sent now, or null when it can (plan 16, WP-08
 * "For WP-11": disabled while ONCALL_ENABLED is off or ONCALL_PUBLIC_API_URL
 * is unset; also without the Twilio account). null health (not loaded, or the
 * read failed) is not a reason: the server refuses with its own message.
 */
export function testCallBlockReason(health: OncallHealth | null): string | null {
  if (!health) return null;
  if (!health.switches?.enabled) {
    return 'Test calls are off while the line is switched off (ONCALL_ENABLED). The launch runbook turns it on.';
  }
  if (!health.switches?.public_api_url_configured) {
    return 'Test calls need ONCALL_PUBLIC_API_URL on the api. The launch runbook sets it.';
  }
  if (health.twilio_configured === false) {
    return 'Test calls need the Twilio account (TWILIO_ACCOUNT_SID and TWILIO_AUTH_TOKEN) on the api.';
  }
  return null;
}
